import { AutoComplete, Form, FormRule, Input } from 'antd';
import { requiredRule } from 'const/formRules';
import { useDepartmentCodeByPostalCode } from 'hooks/dossiers.hooks';
import { useAddressAutocomplete } from 'hooks/useAddressAutocomplete';
import { t } from 'i18next';
import React, { useRef } from 'react';

export interface AddressFormFieldsConfig {
  fieldNames: {
    address: string;
    postalCode: string;
    city: string;
  };
  labels: {
    address: string;
    postalCode: string;
    city: string;
  };
  placeholders: {
    address: string;
    postalCode: string;
    city: string;
  };
  required?: {
    address?: boolean;
    postalCode?: boolean;
    city?: boolean;
  };
  postalCodeRules?: FormRule[];
  validateDepartmentCode?: boolean;
  queryKey?: string;
}

interface AddressFormFieldsProps {
  config: AddressFormFieldsConfig;
}

/**
 * Composant réutilisable de champs de formulaire d'adresse avec fonctionnalité d'autocomplétion.
 *
 * Ce composant affiche trois champs de formulaire : adresse, code postal et ville.
 * Il utilise l'API d'adresses du gouvernement français pour les suggestions d'autocomplétion
 * et remplit automatiquement le code postal et la ville lorsqu'une adresse est sélectionnée.
 *
 * @param config - Objet de configuration spécifiant les noms de champs, les libellés, les placeholders et les options de validation
 *
 */
export default function AddressFormFields({ config }: AddressFormFieldsProps) {
  const form = Form.useFormInstance();
  const codePostal = Form.useWatch(config.fieldNames.postalCode, form);
  const { refetch } = useDepartmentCodeByPostalCode(codePostal as string);
  const lastValidatedPostalCode = useRef<string | null>(null);
  const isAddressRequired = config.required?.address ?? true;
  const isPostalCodeRequired = config.required?.postalCode ?? true;
  const isCityRequired = config.required?.city ?? true;

  const {
    addressOptions,
    isSearching,
    handleAddressSearch,
    handleAddressSelect,
  } = useAddressAutocomplete({
    fieldMapping: {
      address: config.fieldNames.address,
      postalCode: config.fieldNames.postalCode,
      city: config.fieldNames.city,
    },
    queryKey: config.queryKey,
  });

  // Build postal code validation rules
  const postalCodeRules: FormRule[] = [
    ...(isPostalCodeRequired ? [requiredRule] : []),
    {
      pattern: /^[0-9]{5}$/,
      message: t('nouveau_formulaire:code_postal:error'),
    },
    ...(config.postalCodeRules || []),
  ];

  // Add department code validation if enabled
  if (config.validateDepartmentCode !== false) {
    postalCodeRules.push({
      validator: async (_rule, value) => {
        if (!value || !/^[0-9]{5}$/.test(value)) {
          return Promise.resolve();
        }

        // Only refetch if the postal code has changed
        if (lastValidatedPostalCode.current === value) {
          return Promise.resolve();
        }

        const response = await refetch();

        if (!response.isError) {
          lastValidatedPostalCode.current = value;
        }

        return response.isError
          ? Promise.reject(response.error)
          : Promise.resolve();
      },
    });
  }

  return (
    <>
      <Form.Item
        label={config.labels.address}
        name={config.fieldNames.address}
        rules={isAddressRequired ? [requiredRule] : []}
        required={isAddressRequired}
      >
        <AutoComplete
          options={addressOptions}
          onSearch={handleAddressSearch}
          onSelect={handleAddressSelect}
          placeholder={config.placeholders.address}
          size="large"
          notFoundContent={
            isSearching
              ? t('global:loading')
              : addressOptions.length === 0
                ? null
                : t('global:no_results')
          }
        />
      </Form.Item>

      <Form.Item
        label={config.labels.postalCode}
        name={config.fieldNames.postalCode}
        rules={postalCodeRules}
        required={isPostalCodeRequired}
      >
        <Input placeholder={config.placeholders.postalCode} size="large" />
      </Form.Item>

      <Form.Item
        label={config.labels.city}
        name={config.fieldNames.city}
        rules={isCityRequired ? [requiredRule] : []}
        required={isCityRequired}
      >
        <Input placeholder={config.placeholders.city} size="large" />
      </Form.Item>
    </>
  );
}
